/* eslint-disable max-lines-per-function */
import { t } from '@deltasierra/shared';
import * as React from 'react';
import { ButtonProps, Button } from '../Button';
import { FormGroup } from '../Form';
import { Translate } from '../Translate';

export interface OnCompleteStepBoolean<T> {
    type: 'boolean';
    (data: T): boolean;
}

export interface OnCompleteStepAny<T> {
    type?: 'unknown' | undefined;
    (data: T): unknown;
}

export interface OnCompleteStepSetState<T> extends React.Dispatch<React.SetStateAction<T | undefined>> {
    type: 'dispatch';
    (data: T): void;
}

export type OnCompleteStep<T> = OnCompleteStepAny<T> | OnCompleteStepBoolean<T> | OnCompleteStepSetState<T>;

export interface WizardStep<T> {
    renderStep: (props: WizardStepProps<T>) => JSX.Element;
    onComplete?: OnCompleteStep<T>;
}

export function createWizardStep<T>(
    renderStep: (props: WizardStepProps<T>) => JSX.Element,
    onComplete?: OnCompleteStep<T>,
): WizardStep<T> {
    const step: WizardStep<T> = {
        onComplete,
        renderStep,
    };
    return step;
}

export type PassableButtonProps = Partial<ButtonProps>;

export type PrevButtonProps = PassableButtonProps;

export type NextButtonProps<T> = PassableButtonProps & {
    dataToSave?: T;
};

export type PrevAndNextButtonProps<T> = Pick<NextButtonProps<T>, 'dataToSave'> & {
    prev?: PrevButtonProps;
    next?: Omit<NextButtonProps<T>, 'dataToSave'>;
};

export type WizardStepProps<T = void> = {
    gotoNextStep: (values: T) => void;
    gotoPrevStep: () => void;
    exitWizard: () => void;
    nextButtonProps: NextButtonProps<T>;
    prevButtonProps: PrevButtonProps;
    PrevAndNextButtons: React.FC<PrevAndNextButtonProps<T>>;
    ButtonsAndStepCount: React.FC<PrevAndNextButtonProps<T>>;
    StepCount: React.FC;
    CancelButton: React.FC<NextButtonProps<T>>;
    NextButton: React.FC<NextButtonProps<T>>;
    PrevButton: React.FC<PrevButtonProps>;
};

export type WizardProps = {
    currentStep?: number;
    steps: Array<WizardStep<any>>;
    prevLabel?: string;
    nextLabel?: string;
    cancelLabel?: string;
    lastNextLabel?: string;
    onCancel?: () => void;
    onSubmit?: () => void;
};

export const Wizard: React.FC<WizardProps> = ({
    cancelLabel = t('COMMON.CANCEL'),
    currentStep: _currentStepId = 0,
    lastNextLabel = t('COMMON.SUBMIT'),
    nextLabel = t('COMMON.NEXT'),
    onCancel,
    onSubmit,
    prevLabel = t('COMMON.PREVIOUS'),
    steps,
}) => {
    const totalSteps = steps.length;

    const [currentStepId, updateCurrentStepNumber] = React.useState(_currentStepId);

    const currentStepNumber = currentStepId + 1;

    const currentStep = steps[currentStepId];

    const hasPrevStep = currentStepId > 0;
    const hasNextStep = currentStepId < totalSteps - 1;

    function cancel() {
        onCancel?.();
    }

    function gotoPrev() {
        updateCurrentStepNumber(currentStepId - 1);
    }

    function gotoNext<T>(dataToSave?: T) {
        if (currentStep.onComplete) {
            if (currentStep.onComplete.type === 'boolean') {
                if (!currentStep.onComplete(dataToSave)) {
                    return;
                }
            } else {
                currentStep.onComplete(dataToSave);
            }
        }
        if (hasNextStep) {
            updateCurrentStepNumber(currentStepId + 1);
        } else {
            onSubmit?.();
        }
    }

    const defaultPrevButtonProps: PrevButtonProps = {
        spaceLeft: true,
        type: 'button',
    };

    const defaultCancelButtonProps: PrevButtonProps = {
        spaceLeft: true,
        type: 'button',
    };

    const defaultNextButtonProps: NextButtonProps<void> = {
        spaceLeft: true,
        type: 'button',
    };

    function CancelButton({ children, ...props }: PrevButtonProps) {
        return (
            <Button onClick={cancel} {...defaultCancelButtonProps} {...props}>
                {cancelLabel}
            </Button>
        );
    }

    function PrevButton({ children, ...props }: PrevButtonProps) {
        return hasPrevStep ? (
            <Button onClick={gotoPrev} {...defaultPrevButtonProps} {...props}>
                {children ?? prevLabel}
            </Button>
        ) : (
            <CancelButton {...props}>{children}</CancelButton>
        );
    }

    function NextButton<T>({ children, dataToSave, ...props }: NextButtonProps<T>) {
        return (
            <Button onClick={() => gotoNext(dataToSave)} {...defaultNextButtonProps} {...props}>
                {children ?? (hasNextStep ? nextLabel : lastNextLabel)}
            </Button>
        );
    }

    function PrevAndNextButtons<T>({ dataToSave, next, prev }: PrevAndNextButtonProps<T>) {
        return (
            <>
                <PrevButton {...prev} />
                <NextButton dataToSave={dataToSave} {...next} />
            </>
        );
    }

    function StepCount() {
        return <Translate keyId="COMMON.STEP_N_OF_COUNT" options={{ count: totalSteps, step: currentStepNumber }} />;
    }

    function ButtonsAndStepCount<T>(props: PrevAndNextButtonProps<T>) {
        return (
            <FormGroup fullWidth>
                <div className="pull-left">
                    <StepCount />
                </div>
                <div className="pull-right">
                    <PrevAndNextButtons {...props} />
                </div>
            </FormGroup>
        );
    }

    return currentStep.renderStep({
        ButtonsAndStepCount,
        CancelButton,
        NextButton,
        PrevAndNextButtons,
        PrevButton,
        StepCount,
        exitWizard: cancel,
        gotoNextStep: gotoNext,
        gotoPrevStep: gotoPrev,
        nextButtonProps: defaultPrevButtonProps,
        prevButtonProps: defaultPrevButtonProps,
    });
};

Wizard.displayName = 'Wizard';
